import { api } from './api';

// Manufacturing service for work orders and BOMs

export interface WorkOrder {
  id: number;
  plantId?: string | null;
  workOrderNumber: string;
  product: {
    id: string;
    name: string;
    sku: string;
  };
  productId: string;
  bomId?: number;
  // The engineering BOM the materials were taken from
  bomHeaderId?: string | null;
  bomHeaderNumber?: string | null;
  plannedQuantity: number;
  completedQuantity: number;
  rejectedQuantity: number;
  unit?: string;
  status: 'draft' | 'planned' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  productionLine?: string;
  batchNumber?: string;
  notes?: string;
  qualityNotes?: string;
  estimatedCost: number;
  actualCost: number;
  laborHours: number;
  machineHours: number;
  salesOrderReference?: string;
  customerReference?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface WorkOrderQueryParams {
  page: number;
  limit: number;
  search?: string;
  status?: WorkOrder['status'];
  priority?: WorkOrder['priority'];
}

export interface PaginatedWorkOrders {
  data: WorkOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface WorkOrderSummary {
  total: number;
  inProgress: number;
  completed: number;
  overdue: number;
}

export interface CreateWorkOrderRequest {
  plantId?: string;
  workOrderNumber: string;
  productId: string;
  bomId?: number;
  bomHeaderId?: string;
  plannedQuantity: number;
  unit?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  plannedStartDate: string;
  plannedEndDate: string;
  productionLine?: string;
  batchNumber?: string;
  notes?: string;
  estimatedCost?: number;
  laborHours?: number;
  machineHours?: number;
  salesOrderReference?: string;
  customerReference?: string;
  materialConsumptions?: {
    materialId: string;
    plannedQuantity: number;
    unit?: string;
    unitCost?: number;
  }[];
}

export interface BOM {
  id: number;
  bomNumber: string;
  productId: string;
  product: {
    id: string;
    name: string;
    sku: string;
  };
  version: string;
  isActive: boolean;
  totalCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBOMRequest {
  bomNumber: string;
  productId: string;
  version: string;
  materials: {
    materialId: string;
    quantity: number;
    unit?: string;
    unitCost?: number;
  }[];
}

class ManufacturingService {
  // Work Order methods
  async getWorkOrders(): Promise<WorkOrder[]> {
    const response = await api.get('/manufacturing/work-orders');
    return response.data;
  }

  async getWorkOrdersPage(query: WorkOrderQueryParams): Promise<PaginatedWorkOrders> {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.priority ? { priority: query.priority } : {}),
    };
    const response = await api.get<PaginatedWorkOrders>('/manufacturing/work-orders/paginated', { params });
    return response.data;
  }

  async getWorkOrderSummary(): Promise<WorkOrderSummary> {
    const response = await api.get<WorkOrderSummary>('/manufacturing/work-orders/summary');
    return response.data;
  }

  async getWorkOrder(id: number): Promise<WorkOrder> {
    const response = await api.get(`/manufacturing/work-orders/${id}`);
    return response.data;
  }

  async createWorkOrder(data: CreateWorkOrderRequest): Promise<WorkOrder> {
    const response = await api.post('/manufacturing/work-orders', data);
    return response.data;
  }

  async updateWorkOrder(
    id: number,
    data: Partial<CreateWorkOrderRequest> & { status?: WorkOrder['status'] },
  ): Promise<WorkOrder> {
    const response = await api.patch(`/manufacturing/work-orders/${id}`, data);
    return response.data;
  }

  async deleteWorkOrder(id: number): Promise<void> {
    await api.delete(`/manufacturing/work-orders/${id}`);
  }

  async startWorkOrder(id: number): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/start`);
    return response.data;
  }

  async completeWorkOrder(
    id: number,
    completedQuantity: number,
    rejectedQuantity = 0,
    batches: { materialBatches?: { materialId: string; batchId: string; quantity: number }[]; batchNumber?: string; heatNumber?: string; serialNumbers?: string[] } = {},
  ): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/complete`, {
      completedQuantity,
      rejectedQuantity,
      ...batches,
    });
    return response.data;
  }

  // BOM methods
  async getBOMs(): Promise<BOM[]> {
    const response = await api.get('/manufacturing/bom');
    return response.data;
  }

  async getBOM(id: number): Promise<BOM> {
    const response = await api.get(`/manufacturing/bom/${id}`);
    return response.data;
  }

  async createBOM(data: CreateBOMRequest): Promise<BOM> {
    const response = await api.post('/manufacturing/bom', data);
    return response.data;
  }

  async updateBOM(id: number, data: Partial<CreateBOMRequest>): Promise<BOM> {
    const response = await api.patch(`/manufacturing/bom/${id}`, data);
    return response.data;
  }

  async deleteBOM(id: number): Promise<void> {
    await api.delete(`/manufacturing/bom/${id}`);
  }

  async calculateBOMCost(id: number): Promise<{ bomId: number; totalCost: number }> {
    const response = await api.get(`/manufacturing/bom/${id}/cost`);
    return response.data;
  }
}

export const manufacturingService = new ManufacturingService();